import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import FilterButton from './FilterButton';
import FilterPopup from './FilterPopup';
import FilterModal from './FilterModal';
import IndustryModal from '../ui/IndustryModal';
import SpecializationModal from '../ui/SpecializationModal';
import ExperienceModal from '../ui/ExperienceModal';
import AreaModal from '../ui/AreaModal';
import { FiltersState } from '../../hooks/useFilters';
import { getIndustryNameById } from '../../services/hhDictionariesService';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { formatSalaryDisplay } from '../../utils/numberFormat';
import { extractLastPathSegment } from '../../utils/filterUtils';
import { getSourceConfig } from '../../sources';
import {
  getSpecializationDisplayCacheKey,
  getSpecializationDisplayName,
} from '../../sources/specializationDisplay';
import {
  hasAvitoExperienceValue,
  normalizeAvitoExperienceValue,
} from '../../sources/avito/experienceRange';

interface FilterChip {
  id: string;
  label: string;
  value: string;
  onRemove: () => void;
  onAdd?: () => void;
}

interface SearchHeaderProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  onToggleFilters: (section?: string) => void;
  filtersOpen: boolean;
  filters: FiltersState;
  onFiltersChange: (filters: Partial<FiltersState>) => void;
  filterChips: FilterChip[];
  onChipClick?: (chipId: string) => void;
  sourceDropdown?: React.ReactNode;
}

export const SearchHeader: React.FC<SearchHeaderProps> = ({
  searchValue,
  onSearchChange,
  onSearch,
  onClearFilters,
  onToggleFilters,
  filtersOpen,
  filters,
  sourceDropdown,
  onFiltersChange,
  filterChips,
  onChipClick
}) => {
  const colors = useBrandingColors();
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [modalFilter, setModalFilter] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const filterRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // Состояние для новых модальных окон
  const [isIndustryModalOpen, setIsIndustryModalOpen] = useState(false);
  const [isSpecializationModalOpen, setIsSpecializationModalOpen] = useState(false);
  const [isExperienceModalOpen, setIsExperienceModalOpen] = useState(false);
  const [isAreaModalOpen, setIsAreaModalOpen] = useState(false);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedSpecializations, setSelectedSpecializations] = useState<string[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [industryNames, setIndustryNames] = useState<Record<string, string>>({});
  const [areaNames, setAreaNames] = useState<Record<string, string>>({});
  const [specializationNames, setSpecializationNames] = useState<Record<string, string>>({});
  const [geographyLabel, setGeographyLabel] = useState<string>('География');

  const getSkillGroupsCount = () => {
    const groups = (filters as any).skillGroups;
    if (!Array.isArray(groups) || groups.length === 0) return 0;
    return groups.reduce((sum: number, g: any) => sum + (Array.isArray(g?.skills) ? g.skills.length : 0), 0);
  };

  // Конфигурация текущего источника для фильтрации кнопок и отображения
  // source-specific справочников.
  const sourceConfig = getSourceConfig(filters.source || 'hh');
  const hasSection = (section: string) => sourceConfig.filterSections.includes(section as any);

  // --- Фиксация полосы фильтров при прокрутке ---
  const containerRef = useRef<HTMLDivElement | null>(null);
  const filterBarRef = useRef<HTMLDivElement | null>(null);
  const scrollParentRef = useRef<HTMLElement | Window | null>(null);
  const [isFixed, setIsFixed] = useState(false);
  const [fixedRect, setFixedRect] = useState<{ left: number; top: number; width: number; height: number }>({ left: 0, top: 0, width: 0, height: 0 });
  const anchorRef = useRef<HTMLDivElement | null>(null);

  const getScrollParent = (node: HTMLElement | null): HTMLElement | Window => {
    let el: HTMLElement | null = node;
    while (el && el.parentElement) {
      const style = window.getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') return el;
      el = el.parentElement;
    }
    return window;
  };

  useEffect(() => {
    const filterBar = filterBarRef.current;
    const container = containerRef.current;
    const anchor = anchorRef.current;
    if (!filterBar || !container || !anchor) return;

    const scrollEl = getScrollParent(containerRef.current);
    scrollParentRef.current = scrollEl;

    const update = () => {
      const bar = filterBarRef.current;
      const cont = containerRef.current;
      const anchor = anchorRef.current;
      if (!bar || !cont || !anchor) return;

      const barRect = bar.getBoundingClientRect();
      const contRect = cont.getBoundingClientRect();
      const topEdge = scrollEl instanceof Window ? 0 : (scrollEl as HTMLElement).getBoundingClientRect().top;
      const anchorRect = anchor.getBoundingClientRect();

      const shouldFix = anchorRect.top <= topEdge + 0.5;

      if (shouldFix) {
        const left = contRect.left; // используем точные границы контейнера
        const width = contRect.width; // ширина идентична обычному состоянию (w-full)
        const top = topEdge;
        setFixedRect({ left, top, width, height: barRect.height });
        if (!isFixed) setIsFixed(true);
      } else {
        if (isFixed) setIsFixed(false);
      }
    };

    const onScroll = () => update();
    const onResize = () => update();

    if (scrollEl instanceof Window) {
      window.addEventListener('scroll', onScroll, { passive: true });
    } else {
      (scrollEl as HTMLElement).addEventListener('scroll', onScroll, { passive: true });
    }
    window.addEventListener('resize', onResize);

    // начальная позиция
    update();

    return () => {
      if (scrollEl instanceof Window) {
        window.removeEventListener('scroll', onScroll);
      } else {
        (scrollEl as HTMLElement).removeEventListener('scroll', onScroll);
      }
      window.removeEventListener('resize', onResize);
    };
  }, [isFixed]);

  // Синхронизация локальных состояний с фильтрами
  useEffect(() => {
    setSelectedIndustries((filters as any).selectedIndustries || []);
    setSelectedSpecializations((filters as any).selectedSpecializations || []);
    setSelectedAreas(filters.cityIds || []);
  }, [filters]);

  useEffect(() => {
    const loadIndustryNames = async () => {
      const selectedIndustries = (filters as any).selectedIndustries || [];
      if (selectedIndustries.length === 0) return;
      
      setIndustryNames(prev => {
        const idsToLoad = selectedIndustries.filter((id: string) => !prev[id]);
        if (idsToLoad.length === 0) return prev;
        
        Promise.all(
          idsToLoad.map(async (industryId: string) => {
            try {
              const name = await getIndustryNameById(String(industryId).trim());
              return { id: industryId, name: name && name !== industryId ? name : industryId };
            } catch (error) {
              console.error('Error loading industry name:', error);
              return { id: industryId, name: String(industryId) };
            }
          })
        ).then(results => {
          const newNames: Record<string, string> = {};
          results.forEach(({ id, name }) => {
            newNames[id] = name;
          });
          if (Object.keys(newNames).length > 0) {
            setIndustryNames(current => ({ ...current, ...newNames }));
          }
        });
        
        return prev;
      });
    };

    void loadIndustryNames();
  }, [(filters as any).selectedIndustries]);

  // Загрузка названий областей
  useEffect(() => {
    const loadAreaNames = async () => {
      const selectedAreaIds = filters.cityIds || [];
      const newNames: Record<string, string> = {};
      const resolveAreaName = sourceConfig.postProcessors?.getAreaNameById;
      
      for (let index = 0; index < selectedAreaIds.length; index += 1) {
        const areaId = selectedAreaIds[index];
        if (!areaNames[areaId]) {
          try {
            const name = resolveAreaName
              ? await resolveAreaName(areaId)
              : (filters.cities?.[index] || areaId);
            newNames[areaId] = name;
          } catch (error) {
            console.error('Error loading area name:', error);
            newNames[areaId] = areaId;
          }
        }
      }
      
      if (Object.keys(newNames).length > 0) {
        setAreaNames(prev => ({ ...prev, ...newNames }));
      }
    };

    void loadAreaNames();
  }, [filters, areaNames, sourceConfig.postProcessors]);

  // Вычисление метки географии: показываем количество по сжатому набору (родители),
  // не меняя сами выбранные значения
  useEffect(() => {
    const computeGeoLabel = async () => {
      const ids = filters.cityIds || [];
      if (!Array.isArray(ids) || ids.length === 0) {
        setGeographyLabel('География');
        return;
      }
      try {
        const compressAreaIds = sourceConfig.postProcessors?.compressCityIds
          || (async (values: string[]) => values);
        const resolveAreaName = sourceConfig.postProcessors?.getAreaNameById;
        const compressed = await compressAreaIds(ids);
        const count = Array.isArray(compressed) ? compressed.length : ids.length;
        if (count === 1) {
          const onlyId = (compressed as string[])[0];
          const name = resolveAreaName
            ? await resolveAreaName(onlyId)
            : (filters.cities?.[0] || areaNames[onlyId] || onlyId);
          setGeographyLabel(name || 'География');
        } else {
          setGeographyLabel(`География ${count}`);
        }
      } catch {
        setGeographyLabel(`География ${ids.length}`);
      }
    };
    void computeGeoLabel();
  }, [filters.cityIds, filters.cities, areaNames, sourceConfig.postProcessors]);

  useEffect(() => {
    const loadSpecializationNames = async () => {
      const sourceId = filters.source || 'hh';
      const selectedSpecializationIds = (filters as any).selectedSpecializations || [];
      if (selectedSpecializationIds.length === 0) return;
      
      setSpecializationNames(prev => {
        const idsToLoad = selectedSpecializationIds.filter((id: string) => {
          const cacheKey = getSpecializationDisplayCacheKey(sourceId, id);
          return !prev[cacheKey];
        });
        if (idsToLoad.length === 0) return prev;
        
        Promise.all(
          idsToLoad.map(async (specializationId: string) => {
            const cacheKey = getSpecializationDisplayCacheKey(sourceId, specializationId);
            try {
              const name = await getSpecializationDisplayName(sourceId, specializationId);
              return { cacheKey, name: name || String(specializationId) };
            } catch (error) {
              console.error('Error loading specialization name:', error);
              return { cacheKey, name: String(specializationId) };
            }
          })
        ).then(results => {
          const newNames: Record<string, string> = {};
          results.forEach(({ cacheKey, name }) => {
            newNames[cacheKey] = name;
          });
          if (Object.keys(newNames).length > 0) {
            setSpecializationNames(current => ({ ...current, ...newNames }));
          }
        });
        
        return prev;
      });
    };

    void loadSpecializationNames();
  }, [(filters as any).selectedSpecializations, filters.source]);

  // Фильтры для модального окна справа (большие списки с поиском)
  const modalFilters = ['geography'];
  
  // Фильтры для popup (небольшое количество опций)
  const popupFilters = ['hh-params', 'employment', 'salary', 'age', 'avito-experience'];
  
  // Фильтры для центрального модального окна (сложные фильтры)
  const centerModalFilters = ['experience'];

  // Кнопки фильтров, отфильтрованные по доступным секциям источника
  const defaultFilterOptions = [
    { id: 'geography', label: 'География' },
    ...(hasSection('industry') ? [{ id: 'industry', label: 'Отрасль' }] : []),
    ...(hasSection('specialization') ? [{ id: 'specialization', label: 'Специализация' }] : []),
    ...(hasSection('positionExperience') || hasSection('experiencePeriod') ? [{ id: 'experience', label: 'Опыт работы' }] : []),
    ...(hasSection('jobSearchStatus') ? [{ id: 'hh-params', label: 'Статус поиска' }] : []),
    ...(hasSection('employmentType') ? [{ id: 'employment', label: 'Тип занятости' }] : []),
    { id: 'salary', label: 'Уровень дохода' },
    ...(hasSection('age') ? [{id: 'age', label: 'Возраст'}] : []),
    ...(hasSection('avitoExperienceRange') ? [{ id: 'avito-experience', label: 'Стаж работы' }] : []),
    // Skills/Компании скрываем для источников где их в принципе нет (Avito).
    // У HH/E-Staff они в filterSections — см. hh/config.ts и estaff/config.ts.
    ...(hasSection('skills') ? [{ id: 'skills', label: 'Навыки' }] : []),
    ...(hasSection('companies') ? [{ id: 'companies', label: 'Компании' }] : []),
  ];

  // Функция для умного отображения фильтров
  const getSmartFilterLabel = (optionId: string, hasActiveFilters: boolean) => {
    if (!hasActiveFilters) {
      const labels: Record<string, string> = {
        'geography': 'География',
        'industry': 'Отрасль',
        'specialization': 'Специализация',
        'hh-params': 'Статус поиска',
        'experience': 'Опыт работы',
        'employment': 'Тип занятости',
        'salary': 'Уровень дохода',
        'skills': 'Навыки',
        'companies': 'Компании',
        'age' : 'Возраст',
        'avito-experience': 'Стаж работы',
      };
      return labels[optionId] || optionId;
    }

    switch (optionId) {
      case 'geography': {
        return geographyLabel;
      }
      case 'industry': {
        const selectedIndustries = (filters as any).selectedIndustries || [];
        const totalCount = selectedIndustries.length;
        
        if (totalCount === 0) return 'Отрасль';
        if (totalCount === 1) {
          const id = selectedIndustries[0];
          const nameFromMap = industryNames[id];
          return nameFromMap || id || 'Отрасль';
        }
        return `Отрасли ${totalCount}`;
      }
      
      case 'specialization': {
        const selectedSpecializations = (filters as any).selectedSpecializations || [];
        const totalCount = selectedSpecializations.length;
        
        if (totalCount === 0) return 'Специализация';
        if (totalCount === 1) {
          const id = selectedSpecializations[0];
          const cacheKey = getSpecializationDisplayCacheKey(filters.source || 'hh', id);
          const nameFromMap = specializationNames[cacheKey];
          return nameFromMap || extractLastPathSegment(String(id)) || 'Специализация';
        }
        return `Специализации ${totalCount}`;
      }
      
      case 'experience': {
        const positionExp = filters.positionExperience || '';
        const totalExp = filters.totalExperience || '';
        const selectedCount = [
          positionExp && positionExp !== '',
          totalExp && totalExp !== ''
        ].filter(Boolean).length;

        if (selectedCount === 0) return 'Опыт работы';
        return `Опыт работы: ${selectedCount}`;
      }
      
      case 'salary': {
        const hasFrom = filters.salaryFrom && filters.salaryFrom !== '';
        const hasTo = filters.salaryTo && filters.salaryTo !== '';
        const cur = filters.salaryCurrency || 'RUR';

        if (!hasFrom && !hasTo) return 'Уровень дохода';
        if (hasFrom && hasTo) return `${formatSalaryDisplay(filters.salaryFrom)} - ${formatSalaryDisplay(filters.salaryTo)} ${cur}`;
        if (hasFrom) return `от ${formatSalaryDisplay(filters.salaryFrom)} ${cur}`;
        if (hasTo) return `до ${formatSalaryDisplay(filters.salaryTo)} ${cur}`;
        return 'Уровень дохода';
      }
      
      case 'employment': {
        const types = (filters as any).employmentTypes || [];
        const single = filters.employmentType || '';
        const total = (Array.isArray(types) ? types.length : 0) || (single ? 1 : 0);
        if (total === 0) return 'Тип занятости';
        if (total > 1) return `Типов занятости ${total}`;
        const id = (Array.isArray(types) && types.length === 1) ? types[0] : single;
        const employmentLabels: Record<string, string> = {
          'full': 'Полная занятость',
          'part': 'Частичная занятость',
          'project': 'Проектная работа',
          'volunteer': 'Волонтерство',
          'probation': 'Стажировка'
        };
        return employmentLabels[id] || id;
      }
      
      case 'hh-params': {
        const statuses = filters.jobSearchStatus || [];
        if (statuses.length === 0) return 'Статус поиска';
        if (statuses.length === 1) {
          const statusLabels: Record<string, string> = {
            'active_search': 'Активно ищет работу',
            'looking_for_offers': 'Рассматривает предложения',
            'has_job_offer': 'Предложили работу, решает',
            'accepted_job_offer': 'Вышел на новое место',
            'not_looking_for_job': 'Не ищет работу'
          };
          return statusLabels[statuses[0]] || statuses[0];
        }
        return `Статус поиска ${statuses.length}`;
      }
      
      case 'skills': {
        const skillsAny = filters.skillsAny || [];
        const skillsAll = filters.skillsAll || [];
        const totalSkills = skillsAny.length + skillsAll.length + getSkillGroupsCount();
        
        if (totalSkills === 0) return 'Навыки';
        if (totalSkills === 1) {
          if (skillsAny.length === 1) return skillsAny[0];
          if (skillsAll.length === 1) return skillsAll[0];
        }
        return `Навыки: ${totalSkills}`;
      }
      
      case 'companies': {
        const stopCompanies = filters.stopCompanies || [];
        const donorCompanies = filters.donorCompanies || [];
        const totalCompanies = stopCompanies.length + donorCompanies.length;
        
        if (totalCompanies === 0) return 'Компании';
        if (totalCompanies === 1) {
          if (stopCompanies.length === 1) return `Стоп: ${stopCompanies[0]}`;
          if (donorCompanies.length === 1) return `Донор: ${donorCompanies[0]}`;
        }
        return `Компании: ${totalCompanies}`;
      }

      case 'age': {
        const hasFrom = filters.ageFrom && filters.ageFrom !== '';
        const hasTo = filters.ageTo && filters.ageTo !== '';
        if (!hasFrom && !hasTo) return 'Возраст';
        if (hasFrom && hasTo) return `от ${filters.ageFrom} до ${filters.ageTo}`;
        if (hasFrom) return `от ${filters.ageFrom}`;
        if (hasTo) return `до ${filters.ageTo}`;
        return 'Возраст';
      }

      case 'avito-experience': {
        const min = normalizeAvitoExperienceValue(filters.avitoExperienceMin);
        const max = normalizeAvitoExperienceValue(filters.avitoExperienceMax);
        const hasFrom = hasAvitoExperienceValue(min);
        const hasTo = hasAvitoExperienceValue(max);
        if (!hasFrom && !hasTo) return 'Стаж работы';
        if (hasFrom && hasTo) return `Стаж работы: от ${min} до ${max}`;
        if (hasFrom) return `Стаж работы: от ${min}`;
        if (hasTo) return `Стаж работы: до ${max}`;
        return 'Стаж работы';
      }
      
      default:
        return `+ ${optionId}`;
    }
  };

  const handleFilterClick = (filterId: string) => {
    if (filterId === 'industry') {
      setIsIndustryModalOpen(true);
      setActiveFilter(null);
      setModalFilter(null);
    } else if (filterId === 'geography') {
      setIsAreaModalOpen(true);
      setActiveFilter(null);
      setModalFilter(null);
    } else if (filterId === 'specialization') {
      setIsSpecializationModalOpen(true);
      setActiveFilter(null);
      setModalFilter(null);
    } else if (filterId === 'experience') {
      setIsExperienceModalOpen(true);
      setActiveFilter(null);
      setModalFilter(null);
    } else if (filterId === 'skills') {
      onToggleFilters('skills');
      setActiveFilter(null);
      setModalFilter(null);
    } else if (filterId === 'companies') {
      onToggleFilters('companies');
      setActiveFilter(null);
      setModalFilter(null);
    } else if (modalFilters.includes(filterId)) {
      setModalFilter(modalFilter === filterId ? null : filterId);
      setActiveFilter(null);
    } else if (popupFilters.includes(filterId)) {
      const element = filterRefs.current[filterId];
      if (element) {
        const rect = element.getBoundingClientRect();
        setPopupPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX
        });
      }
      setActiveFilter(activeFilter === filterId ? null : filterId);
      setModalFilter(null);
    }
  };

  const handleClosePopup = () => {
    setActiveFilter(null);
  };

  const handleCloseModal = () => {
    setModalFilter(null);
  };

  return (
    <>
      <div ref={containerRef} className="flex w-full flex-col">
        <div className="box-border w-full rounded-t-[12px] bg-white px-[20px] pb-[0px] pt-[20px]">
          <div className="content-stretch relative flex w-full shrink-0 items-end justify-between">
            <div className="content-stretch relative flex shrink-0 items-end gap-[8px]">
              {sourceDropdown}
              <div className="relative h-11 w-[460px] shrink-0 rounded-[12px] bg-white">
                <div className="relative box-border flex h-11 w-[460px] content-stretch items-center gap-[8px] overflow-clip p-[12px]">
                  <div className="relative size-[20px] shrink-0 overflow-clip">
                    <MagnifyingGlassIcon className={`h-5 w-5 transition-colors duration-200 ${filters.strictSearch ? 'text-[#e16349]' : 'text-[#525866]'}`} />
                  </div>
                  <input
                    type="text"
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        onSearch();
                      }
                    }}
                    placeholder="Поиск"
                    className="relative min-h-px min-w-px basis-0 grow shrink-0 border-none bg-transparent font-inter text-[16px] font-normal not-italic leading-[0] tracking-[-0.084px] text-[#525866] outline-none"
                    style={{ lineHeight: '22px' }}
                  />
                  <button
                    type="button"
                    onClick={() => onFiltersChange({ strictSearch: !filters.strictSearch })}
                    className={`flex items-center gap-[6px] shrink-0 h-[28px] rounded-[8px] transition-all duration-200 ${
                      filters.strictSearch
                        ? 'bg-[#e16349] text-white px-[8px]'
                        : 'bg-transparent text-[#99a1af] hover:bg-[#f5f5f4] hover:text-[#525866] w-[28px] justify-center'
                    }`}
                    title={
                      filters.strictSearch
                        ? 'Строгий поиск: все слова из названия должности'
                        : 'Нестрогий поиск: по должности совпало любое слово'
                    }
                  >
                    {filters.strictSearch && (
                      <span className="font-inter text-[12px] font-medium whitespace-nowrap">строгий</span>
                    )}
                    <svg className="w-[14px] h-[14px] shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                  </button>
                </div>
                <div
                  aria-hidden="true"
                  className={`pointer-events-none absolute inset-0 rounded-[12px] border border-solid transition-colors duration-200 ${
                    filters.strictSearch ? 'border-[#e16349]' : 'border-[#cdd0d5]'
                  }`}
                />
              </div>
              <button
                onClick={onSearch}
                className="relative box-border flex h-11 shrink-0 content-stretch items-center gap-[8px] rounded-[12px] px-[32px] py-[14px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] transition-colors"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primaryHover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary;
                }}
              >
                <div className="relative flex h-[17px] shrink-0 flex-col justify-center font-inter text-[14px] font-medium not-italic leading-[0] text-white">
                  <p className="leading-[20px]">Найти</p>
                </div>
              </button>
            </div>
            <div className="content-stretch relative flex shrink-0 items-center gap-[8px]">
              <button
                onClick={onClearFilters}
                className="relative box-border flex h-11 shrink-0 content-stretch items-center justify-center rounded-[12px] bg-white p-[12px] transition-colors hover:bg-gray-50"
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-[12px] border border-solid border-[#e2e4e9]"
                />
                <div className="relative flex shrink-0 flex-col justify-center font-inter text-[14px] font-normal not-italic leading-[0] text-black">
                  <p className="whitespace-pre leading-[20px]">Сбросить фильтры</p>
                </div>
              </button>

              {/* Filters Toggle Button */}
              <button
                onClick={() => onToggleFilters()}
                className={`relative h-11 shrink-0 rounded-[12px] bg-gray-100 px-[8px] py-0 transition-colors hover:bg-gray-200 ${
                  filtersOpen ? 'bg-gray-200' : ''
                }`}
              >
                <div className="relative box-border flex h-11 items-center overflow-clip px-[8px] py-0">
                  <div className="relative flex size-[20px] shrink-0 items-center justify-center overflow-clip">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <path
                        d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z"
                        stroke="#525866"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="relative box-border flex shrink-0 items-center justify-center gap-[8px] px-[4px] py-0">
                    <div className="relative flex shrink-0 flex-col justify-center font-inter text-center text-[14px] font-medium not-italic leading-[0] text-gray-700">
                      <p className="whitespace-pre leading-[20px]">Фильтры</p>
                    </div>
                  </div>
                </div>
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-[12px] border border-solid border-[#cdd0d5]"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Anchor + Spacer: якорь для определения точки прилипания и компенсация высоты */}
        <div ref={anchorRef} />
        <div style={{ height: isFixed ? fixedRect.height : 0 }} />

        {/* Filter Buttons */}
        <div
          ref={filterBarRef}
          className={`${isFixed ? '' : 'sticky top-0'} z-50 box-border w-full rounded-b-[12px] bg-white px-[20px] pb-[20px] pt-[17px]`}
          style={isFixed ? { position: 'fixed', top: fixedRect.top, left: fixedRect.left, width: fixedRect.width } : undefined}
        >
          <div className="content-stretch relative flex w-full shrink-0 flex-wrap items-center gap-[8px]">
            {/* Active filter chips */}
            {filterChips.map((chip) => (
              <div
                key={chip.id}
                ref={(el) => { filterRefs.current[chip.id] = el; }}
                className="inline-block"
              >
                <FilterButton
                  label={`${chip.label}: ${chip.value}`}
                  isActive={activeFilter === chip.id}
                  onClick={() => handleFilterClick(chip.id)}
                  onRemove={chip.onRemove}
                  showRemove={true}
                />
              </div>
            ))}
            
            {/* Default filter option buttons */}
            {defaultFilterOptions.map((option) => {
              const hasActiveFilters = 
                (option.id === 'geography' && (filters.cities.length > 0 || (filters.cityIds && filters.cityIds.length > 0))) ||
                (option.id === 'industry' && (filters.industry || (filters as any).selectedIndustries?.length > 0)) ||
                (option.id === 'specialization' && (filters.specialization || (filters as any).selectedSpecializations?.length > 0)) ||
                (option.id === 'hh-params' && filters.jobSearchStatus.length > 0) ||
                (option.id === 'experience' && (filters.positionExperience || filters.totalExperience)) ||
                (option.id === 'salary' && (filters.salaryFrom || filters.salaryTo)) ||
                (option.id === 'age' && ((filters.ageFrom && filters.ageFrom !== '') || (filters.ageTo && filters.ageTo !== ''))) ||
                (option.id === 'avito-experience' && (hasAvitoExperienceValue(filters.avitoExperienceMin) || hasAvitoExperienceValue(filters.avitoExperienceMax))) ||
                (option.id === 'employment' && (((filters as any).employmentTypes && (filters as any).employmentTypes.length > 0) || filters.employmentType)) ||
                (option.id === 'skills' && (filters.skillsAny?.length > 0 || filters.skillsAll?.length > 0 || getSkillGroupsCount() > 0)) ||
                (option.id === 'companies' && (filters.stopCompanies?.length > 0 || filters.donorCompanies?.length > 0));

              return (
                <div
                  key={option.id}
                  ref={(el) => { filterRefs.current[option.id] = el; }}
                  className="inline-block"
                >
                  <FilterButton
                    label={getSmartFilterLabel(option.id, !!hasActiveFilters)}
                    isActive={!!hasActiveFilters || (modalFilter === option.id && !['industry', 'specialization'].includes(option.id))}
                    onClick={() => handleFilterClick(option.id)}
                    onRemove={hasActiveFilters ? () => {
                      // Очистка фильтров в зависимости от типа
                      switch (option.id) {
                        case 'geography':
                          onFiltersChange({ cities: [], cityIds: [] });
                          break;
                        case 'industry':
                          onFiltersChange({ industry: '', selectedIndustries: [], experiencePeriod: '' });
                          break;
                        case 'specialization':
                          onFiltersChange({ specialization: '', selectedSpecializations: [] });
                          break;
                        case 'employment':
                          onFiltersChange({ employmentType: '', employmentTypes: [] as any });
                          break;
                        case 'hh-params':
                          onFiltersChange({ jobSearchStatus: [] });
                          break;
                        case 'experience':
                          onFiltersChange({ positionExperience: '', totalExperience: '' });
                          break;
                        case 'salary':
                          onFiltersChange({ salaryFrom: '', salaryTo: '', salaryCurrency: 'RUR' });
                          break;
                        case 'age':
                          onFiltersChange({ ageFrom: '', ageTo: '' });
                          break;
                        case 'avito-experience':
                          onFiltersChange({ avitoExperienceMin: '', avitoExperienceMax: '' });
                          break;
                        case 'skills':
                          onFiltersChange({ skillsAny: [], skillsAll: [], skillGroups: [] as any });
                          break;
                        case 'companies':
                          onFiltersChange({ stopCompanies: [], donorCompanies: [] });
                          break;
                      }
                    } : undefined}
                    showRemove={!!hasActiveFilters}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Filter Popup */}
      {activeFilter && (
        <FilterPopup
          filterType={activeFilter}
          isOpen={!!activeFilter}
          onClose={handleClosePopup}
          filters={filters}
          onFiltersChange={onFiltersChange}
          position={popupPosition}
        />
      )}

      {/* Filter Modal */}
      {modalFilter && (
        <FilterModal
          isOpen={!!modalFilter}
          onClose={handleCloseModal}
          filterType={modalFilter}
          filters={filters}
          onFiltersChange={onFiltersChange}
        />
      )}

      {/* Industry Modal - рендерится на уровне всей страницы */}
      <IndustryModal
        isOpen={isIndustryModalOpen}
        onClose={() => setIsIndustryModalOpen(false)}
        selectedIndustries={selectedIndustries}
        filterByExpIndustry={(filters as any).filterByExpIndustry || false}
        experiencePeriod={filters.experiencePeriod || ''}
        onSelectionChange={(industries, filterByExpIndustry, experiencePeriod) => {
          setSelectedIndustries(industries);
          // Сбрасываем фильтр если отрасли не выбраны
          const effectiveFilter = industries.length === 0 ? false : (filterByExpIndustry ?? (filters as any).filterByExpIndustry ?? false);
          onFiltersChange({ selectedIndustries: industries, filterByExpIndustry: effectiveFilter, experiencePeriod: experiencePeriod ?? filters.experiencePeriod ?? '' });
        }}
      />

      {/* Specialization Modal - рендерится на уровне всей страницы.
          fetchData и mode берутся из sourceConfig (hh — иерархия hh-словаря,
          avito — плоский specialization-справочник). Compunent не знает источника. */}
      <SpecializationModal
        isOpen={isSpecializationModalOpen}
        onClose={() => setIsSpecializationModalOpen(false)}
        selectedSpecializations={selectedSpecializations}
        onSelectionChange={(specializations) => {
          setSelectedSpecializations(specializations);
          onFiltersChange({ selectedSpecializations: specializations });
        }}
        fetchData={sourceConfig.dataLoaders?.specialization}
        mode={sourceConfig.uiHints?.specializationMode}
      />

      {/* Experience Modal - рендерится на уровне всей страницы */}
      <ExperienceModal
        isOpen={isExperienceModalOpen}
        onClose={() => setIsExperienceModalOpen(false)}
        selectedValues={{
          totalExperience: filters.totalExperience || '',
          positionExperience: filters.positionExperience || '',
          experiencePeriod: filters.experiencePeriod || '',
          excludeJumpers: (filters as any).excludeJumpers || false
        }}
        onSelectionChange={(values) => {
          onFiltersChange({
            totalExperience: Array.isArray(values.totalExperience)
              ? values.totalExperience.join(',')
              : values.totalExperience,
            positionExperience: Array.isArray(values.positionExperience)
              ? values.positionExperience.join(',')
              : values.positionExperience,
            excludeJumpers: values.excludeJumpers ?? false
          });
        }}
      />

      {/* Area Modal - рендерится на уровне всей страницы.
          Передаём dataLoaders.locations / hideCountryTabs из sourceConfig
          так же как в FiltersModal — оба входа в "Географию" должны
          работать одинаково для всех источников (hh / avito и т.д.). */}
      <AreaModal
        isOpen={isAreaModalOpen}
        onClose={() => setIsAreaModalOpen(false)}
        selectedAreas={selectedAreas}
        fetchData={sourceConfig.dataLoaders?.locations}
        hideCountryTabs={filters.source === 'avito'}
        onApply={async (areas) => {
          try {
            // Пост-обработка id из sourceConfig.postProcessors (hh/estaff —
            // реальные функции, avito и др. — identity по умолчанию).
            const compressor = sourceConfig.postProcessors?.compressCityIds
              ?? (async (ids: string[]) => ids);
            const resolveName = sourceConfig.postProcessors?.getAreaNameById
              ?? (async (id: string) => id);
            const compressed = await compressor(areas);
            setSelectedAreas(compressed);

            const areaNames = await Promise.all(
              compressed.map(async (areaId) => {
                try {
                  return await resolveName(areaId);
                } catch (error) {
                  console.error('Error loading area name:', error);
                  return areaId;
                }
              })
            );
            
            onFiltersChange({ 
              cityIds: compressed,
              cities: areaNames
            });
          } catch (error) {
            console.error('Error compressing city ids:', error);
            const fallback = (areas || []).map(String);
            setSelectedAreas(fallback);
            const resolveNameFallback = sourceConfig.postProcessors?.getAreaNameById
              ?? (async (id: string) => id);
            const areaNames = await Promise.all(
              fallback.map(async (areaId) => {
                try {
                  return await resolveNameFallback(areaId);
                } catch (e) {
                  console.error('Error loading area name:', e);
                  return areaId;
                }
              })
            );
            onFiltersChange({ cityIds: fallback, cities: areaNames });
          }
        }}
      />
    </>
  );
};

export default SearchHeader;
